import axiosInstance from '../../../main/initializers/axios';
import { ApiConstants } from '../../../main/constants/ApiConstants';
import IProduct from '../../../main/interfaces/IProduct';
import IProductCategory from '../../../main/interfaces/IProductCategory';
import IProductFilter from '../../../main/interfaces/IProductFilter';
import ISubject from '../../../main/interfaces/ISubject';

const DEFAULT_PRODUCT_FILTER: IProductFilter = {
  Search: null,
  Category: null,
  Type: null,
};

const productCache = new Map<string, IProduct[]>();
const productRequests = new Map<string, Promise<IProduct[]>>();

let categoryCache: IProductCategory[] | null = null;
let categoryRequest: Promise<IProductCategory[]> | null = null;

let subjectCache: ISubject[] | null = null;
let subjectRequest: Promise<ISubject[]> | null = null;

const normalizeProductFilter = (filter?: Partial<IProductFilter> | null): IProductFilter => ({
  Type: filter?.Type ?? null,
  Search: filter?.Search ?? null,
  Category: filter?.Category ?? null,
});

const productCacheKey = (filter?: Partial<IProductFilter> | null) => JSON.stringify(normalizeProductFilter(filter));

export const getCachedProducts = async (filter?: Partial<IProductFilter> | null): Promise<IProduct[]> => {
  const request = normalizeProductFilter(filter);
  const key = productCacheKey(request);
  const cached = productCache.get(key);

  if (cached) return cached;

  const pending = productRequests.get(key);
  if (pending) return pending;

  const nextRequest = axiosInstance
    .post(ApiConstants.Products.list, request)
    .then((response) => {
      const data = response.data ?? [];
      productCache.set(key, data);
      productRequests.delete(key);
      return data;
    })
    .catch((error) => {
      productRequests.delete(key);
      throw error;
    });

  productRequests.set(key, nextRequest);
  return nextRequest;
};

export const getCachedProductCategories = async (): Promise<IProductCategory[]> => {
  if (categoryCache) return categoryCache;
  if (categoryRequest) return categoryRequest;

  categoryRequest = axiosInstance
    .get(ApiConstants.Products.productCategory)
    .then((response) => {
      categoryCache = response.data ?? [];
      categoryRequest = null;
      return categoryCache;
    })
    .catch((error) => {
      categoryRequest = null;
      throw error;
    });

  return categoryRequest;
};

export const getCachedSubjects = async (): Promise<ISubject[]> => {
  if (subjectCache) return subjectCache;
  if (subjectRequest) return subjectRequest;

  subjectRequest = axiosInstance
    .get(ApiConstants.invoice.subjects)
    .then((response) => {
      subjectCache = response.data ?? [];
      subjectRequest = null;
      return subjectCache;
    })
    .catch((error) => {
      subjectRequest = null;
      throw error;
    });

  return subjectRequest;
};

export const prefetchRestaurantOrderData = () =>
  Promise.all([
    getCachedProducts(DEFAULT_PRODUCT_FILTER),
    getCachedProductCategories(),
    getCachedSubjects(),
  ]);
